// Money entry, shared by the expense form and the payroll view's invoice cap
// dialog so a dollar amount is typed the same way wherever one is asked for.
//
// The pattern is a pair of values: `raw` is what the number actually is and is
// what gets submitted, `display` is what the field shows. Formatting on every
// keystroke means the raw text has to survive the round trip untouched — a
// half-typed "12." must still read as "$12." on the next render or the decimal
// point is eaten as fast as it is typed.

/** Digits and at most one decimal point. Anything else a paste brings in is dropped. */
export const sanitizeAmount = (value) => {
  if (value == null) return '';
  const onlyAllowed = String(value).replace(/[^0-9.]/g, '');
  const firstDotIndex = onlyAllowed.indexOf('.');
  if (firstDotIndex < 0) return onlyAllowed;
  const before = onlyAllowed.slice(0, firstDotIndex + 1);
  const after = onlyAllowed.slice(firstDotIndex + 1).replace(/\./g, '');
  return `${before}${after}`;
};

export const formatCurrency = (amount) => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

/**
 * The in-progress form: thousands separators and a dollar sign, but the decimals
 * left exactly as typed. Intl would round "12.5" to "$12.50" and put the caret
 * behind a digit the user has not typed yet, so only the integer part goes
 * through it.
 */
export const formatCurrencyTyping = (rawValue) => {
  const raw = sanitizeAmount(rawValue);
  if (!raw) return '';

  const hasDot = raw.includes('.');
  const [intPartRaw, fracPart = ''] = raw.split('.');
  const intPart = intPartRaw === '' ? '0' : intPartRaw;
  const formattedInt = Number(intPart).toLocaleString('en-US', { maximumFractionDigits: 0 });

  if (hasDot) return `$${formattedInt}.${fracPart}`;
  return `$${formattedInt}`;
};
